import { useEditor } from "@craftjs/core";
import { useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { Recording } from "src/types";

import { useBlockSettingsSurfaceOptional } from "@/content-hub/editor/context-menu/block-settings-surface-context";
import { ContextMenuOverlay } from "@/content-hub/editor/context-menu/context-menu-overlay";
import {
  getOrganizationExternalId,
  getPageSlug,
  getPageUrl,
} from "@/content-hub/store/selectors";
import {
  effectiveRecordingDisplay,
  hasCustomRecordingDisplayOverrides,
} from "@/content-hub/theme/primitives/video/recording-metadata-merge";
import {
  displayThumbnailUrlFromUpload,
  RecordingVideoDetailsFields,
} from "@/content-hub/theme/primitives/video/recording-video-details-fields";
import { RecordingLibraryPicker } from "@/content-hub/theme/primitives/video/settings/recording-library-picker";
import { buildContentHubGridVideoShareUrl } from "@/content-hub/utils/build-content-hub-grid-video-share-url";
import { getCraftNodeDom } from "@/content-hub/utils/get-craft-node-dom";
import { resolveVideoBlockPosterForDetails } from "@/content-hub/utils/resolve-video-block-poster-for-details";
import { useAppSelector } from "@/redux/store";

import type { VideoGridProps } from "../video-grid";

// Slide-over for one grid row’s recording title/description/thumbnail + share URL; portaled into
// the block settings surface when available so it covers the full right panel like other overlays.

type ListRecordingItem = Extract<
  NonNullable<VideoGridProps["videoList"]>[number],
  { kind: "recording" }
>;

type VideoGridRecordingMetadataPanelProps = {
  open: boolean;
  panelTitle: string;
  onClose: () => void;
  /** Loaded from store when available; share URL / placeholders fall back to `listItem`. */
  recording?: Recording;
  listIndex: number;
  listItem: ListRecordingItem;
  videoGridCraftId: string;
  setProp: (cb: (props: VideoGridProps) => void) => void;
};

export function VideoGridRecordingMetadataPanel(
  props: VideoGridRecordingMetadataPanelProps
) {
  const {
    open,
    panelTitle,
    onClose,
    recording,
    listIndex,
    listItem,
    videoGridCraftId,
    setProp,
  } = props;

  const { t } = useTranslation();
  const surface = useBlockSettingsSurfaceOptional();
  const { query } = useEditor();
  const organizationExternalId = useAppSelector(getOrganizationExternalId);
  const pageSlug = useAppSelector(getPageSlug);
  const pageUrl = useAppSelector(getPageUrl);

  const mergedDisplay = useMemo(() => {
    if (!recording) {
      return {
        title: listItem.displayTitle ?? "",
        description: listItem.displayDescription ?? "",
        thumbnail: listItem.displayThumbnail ?? "",
      };
    }
    return effectiveRecordingDisplay(recording, listItem);
  }, [recording, listItem]);

  const localReadonlyShareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return buildContentHubGridVideoShareUrl({
      organizationExternalId,
      pageSlug,
      pageUrl,
      getGridRootElement: () => getCraftNodeDom(query, videoGridCraftId),
      video: {
        id: listItem.recordingId,
        title: mergedDisplay.title || null,
      },
    });
  }, [
    organizationExternalId,
    pageSlug,
    pageUrl,
    query,
    videoGridCraftId,
    listItem.recordingId,
    mergedDisplay.title,
  ]);

  if (!open) return null;

  const uploadTxId = `${videoGridCraftId}-${listItem.recordingId}`;

  const patchItem = (patch: Partial<ListRecordingItem>) => {
    setProp((p) => {
      const entry = p.videoList?.[listIndex];
      if (!entry || typeof entry === "string" || entry.kind !== "recording") {
        return;
      }
      Object.assign(entry, patch);
    });
  };

  const handleRecordingIdChange = (nextRecordingId: string) => {
    patchItem({
      recordingId: nextRecordingId,
      displayTitle: undefined,
      displayDescription: undefined,
      displayThumbnail: undefined,
    });
  };

  const panel = (
    <ContextMenuOverlay variant="stack" title={panelTitle} onClose={onClose}>
      <div className="flex flex-col gap-y-4 p-4">
        <RecordingLibraryPicker
          className="border-b border-neutral-b4 pb-4"
          sectionLabel={t("content-hub.preference-panel.video-source")}
          recordingId={listItem.recordingId}
          recording={recording}
          skipSameRecording
          showPreviewThumbnail={false}
          onRecordingSelected={handleRecordingIdChange}
        />
        <RecordingVideoDetailsFields
          analytics={{ kind: "recording", recordingId: listItem.recordingId }}
          localReadonlyShareUrl={localReadonlyShareUrl}
          displayTitle={listItem.displayTitle ?? ""}
          displayDescription={listItem.displayDescription ?? ""}
          displayThumbnail={listItem.displayThumbnail ?? undefined}
          thumbnail={resolveVideoBlockPosterForDetails({
            blockThumbnail: recording?.thumbnail,
          })}
          uploadTransactionId={uploadTxId}
          titlePlaceholder={recording?.title}
          descriptionPlaceholder={recording?.description ?? undefined}
          onTitleChange={(value) =>
            patchItem({ displayTitle: value || undefined })
          }
          onDescriptionChange={(value) =>
            patchItem({ displayDescription: value || undefined })
          }
          onThumbnailUpload={(imageMetadata) =>
            patchItem({
              displayThumbnail: displayThumbnailUrlFromUpload(imageMetadata),
            })
          }
          showReset={hasCustomRecordingDisplayOverrides(listItem)}
          onReset={() =>
            patchItem({
              displayTitle: undefined,
              displayDescription: undefined,
              displayThumbnail: undefined,
            })
          }
        />
      </div>
    </ContextMenuOverlay>
  );

  const portalTarget = surface?.layoutSurfaceRef.current;
  if (portalTarget) {
    return createPortal(panel, portalTarget);
  }

  return panel;
}
